/**
 * O motor de sincronismo.
 *
 * Duas correntes que nunca se misturam:
 *
 *   DESCE  referência (OS abertas, etapas, cadastros, calendário). É somente
 *          leitura no aparelho, então não existe conflito: chegou versão nova,
 *          substitui.
 *
 *   SOBE   a fila de saída. É evento, não linha disputada — ninguém edita a
 *          mesma frase que outro. Sobe em série e some quando o servidor
 *          confirma.
 *
 * **A ORDEM IMPORTA: empurra ANTES de puxar.** Se puxasse primeiro, a etapa
 * baixada viria com o estado antigo e sobrescreveria o apontamento que o
 * operador acabou de fazer na tela — o trabalho dele sumiria diante dos olhos
 * dele.
 *
 * **A marca d'água é por coleção** e só avança quando aquela coleção inteira
 * chegou. Avançar antes deixaria um buraco permanente: o que ficou de fora
 * nunca mais seria pedido.
 *
 * (Mesmo desenho do `sincronismo.ts` do Nexo em Campo.)
 */

import type { Deposito, NomeColecao } from '@pcp/dados';
import { Fila, type ItemDaFila } from './fila.js';

/** O que o aparelho baixa. A ordem importa: cadastro antes de OS. */
export const COLECOES_QUE_DESCEM: readonly NomeColecao[] = [
  'setor',
  'estacao',
  'dePara',
  'motivo',
  'pessoa',
  'cliente',
  'os',
  'osPlano',
  'osEtapa',
  'osMaterial',
  'evento',
] as const;

export interface RespostaDeSubida {
  id: string;
  resultado: 'aceito' | 'duplicado' | 'recusado';
  mensagem?: string;
}

export interface PaginaDeDescida {
  colecao: NomeColecao;
  registros: unknown[];
  /** Ids apagados no servidor — o aparelho tira do banco local. */
  removidos: string[];
  /** Marca d'água nova. Só vale quando `fim` é verdadeiro. */
  marca: string;
  fim: boolean;
}

/**
 * O que o motor precisa de um servidor. Uma interface, não uma URL: é o que
 * permite testar ordem, duplicidade e falha no meio sem subir nada.
 */
export interface Servidor {
  subir(itens: readonly ItemDaFila[]): Promise<RespostaDeSubida[]>;
  baixar(colecao: NomeColecao, marca: string, limite: number): Promise<PaginaDeDescida>;
}

export interface EstadoDoSincronismo {
  emAndamento: boolean;
  ultimoSucessoEm: string | null;
  ultimoErro: string | null;
  pendentes: number;
  travados: number;
}

export interface OpcoesDoSincronismo {
  deposito: Deposito;
  fila: Fila;
  servidor: Servidor;
  online?: () => boolean;
  aoMudar?: (estado: EstadoDoSincronismo) => void;
  agora?: () => number;
  /** Quantos itens sobem por vez. */
  loteDeSubida?: number;
  /** Quantos registros descem por página. */
  paginaDeDescida?: number;
}

const INICIO = '1970-01-01T00:00:00.000Z';
/** Trava para não girar infinito se o servidor devolver sempre a mesma página. */
const MAX_PAGINAS = 40;

export class Sincronismo {
  private deposito: Deposito;
  private fila: Fila;
  private servidor: Servidor;
  private online: () => boolean;
  private aoMudar: (e: EstadoDoSincronismo) => void;
  private agora: () => number;
  private loteDeSubida: number;
  private paginaDeDescida: number;

  private rodando: Promise<void> | null = null;
  private ultimoSucessoEm: string | null = null;
  private ultimoErro: string | null = null;

  constructor(o: OpcoesDoSincronismo) {
    this.deposito = o.deposito;
    this.fila = o.fila;
    this.servidor = o.servidor;
    this.online = o.online ?? (() => true);
    this.aoMudar = o.aoMudar ?? (() => {});
    this.agora = o.agora ?? (() => Date.now());
    this.loteDeSubida = o.loteDeSubida ?? 50;
    this.paginaDeDescida = o.paginaDeDescida ?? 500;
  }

  async estado(): Promise<EstadoDoSincronismo> {
    return {
      emAndamento: this.rodando !== null,
      ultimoSucessoEm: this.ultimoSucessoEm,
      ultimoErro: this.ultimoErro,
      pendentes: await this.fila.contarPendentes(),
      travados: await this.fila.contarTravados(),
    };
  }

  /** Uma rodada. Chamadas concorrentes esperam a mesma rodada. */
  async sincronizar(): Promise<void> {
    if (this.rodando) return this.rodando;
    this.rodando = this.rodada().finally(() => {
      this.rodando = null;
    });
    return this.rodando;
  }

  private async rodada(): Promise<void> {
    if (!this.online()) {
      this.ultimoErro = 'Sem conexão.';
      this.aoMudar(await this.estado());
      return;
    }

    try {
      await this.empurrar();
      await this.puxar();
      this.ultimoSucessoEm = new Date(this.agora()).toISOString();
      this.ultimoErro = null;
    } catch (erro) {
      this.ultimoErro = erro instanceof Error ? erro.message : String(erro);
    }
    this.aoMudar(await this.estado());
  }

  /** Sobe a fila, em série, na ordem em que as coisas aconteceram. */
  private async empurrar(): Promise<void> {
    for (;;) {
      const prontos = (await this.fila.prontos()).slice(0, this.loteDeSubida);
      if (prontos.length === 0) return;

      let respostas: RespostaDeSubida[];
      try {
        respostas = await this.servidor.subir(prontos);
      } catch (erro) {
        // Falha de rede: adia TODOS do lote, sem travar nenhum. A rede voltar
        // resolve; travar aqui encheria a tela do encarregado à toa.
        const mensagem = erro instanceof Error ? erro.message : String(erro);
        for (const item of prontos) await this.fila.falhar(item.id, mensagem);
        throw erro;
      }

      const porId = new Map(respostas.map((r) => [r.id, r]));
      let avancou = false;

      for (const item of prontos) {
        const r = porId.get(item.id);
        if (!r) {
          // O servidor não falou deste item. Tentar de novo é seguro: a chave
          // é a mesma, e repetir é inofensivo.
          await this.fila.falhar(item.id, 'O servidor não respondeu sobre este apontamento.');
          continue;
        }
        if (r.resultado === 'aceito' || r.resultado === 'duplicado') {
          await this.fila.confirmar(item.id);
          avancou = true;
        } else {
          await this.fila.falhar(item.id, r.mensagem ?? 'Recusado pelo servidor.');
        }
      }

      // Nada saiu da fila: insistir só repetiria o mesmo lote para sempre.
      if (!avancou) return;
    }
  }

  /** Baixa o conjunto de trabalho, coleção por coleção. */
  private async puxar(): Promise<void> {
    for (const colecao of COLECOES_QUE_DESCEM) {
      let marca = (await this.lerMarca(colecao)) ?? INICIO;
      let voltas = 0;

      for (;;) {
        voltas += 1;
        if (voltas > MAX_PAGINAS) break;

        const pagina = await this.servidor.baixar(colecao, marca, this.paginaDeDescida);

        if (pagina.registros.length > 0) {
          await this.deposito.gravarLote(colecao, pagina.registros);
        }
        for (const id of pagina.removidos) {
          await this.deposito.apagar(colecao, id);
        }

        // A marca só avança quando a coleção inteira chegou. Avançar no meio
        // deixaria um buraco que nunca mais seria pedido.
        if (pagina.fim) {
          marca = pagina.marca;
          await this.gravarMarca(colecao, marca);
          break;
        }
        if (pagina.marca === marca && pagina.registros.length === 0) break;
        marca = pagina.marca;
      }
    }
  }

  private async lerMarca(colecao: NomeColecao): Promise<string | null> {
    const c = await this.deposito.ler<{ chave: string; valor: string }>(
      'config',
      `marca.${colecao}`,
    );
    return c?.valor ?? null;
  }

  private async gravarMarca(colecao: NomeColecao, marca: string): Promise<void> {
    await this.deposito.gravar('config', { chave: `marca.${colecao}`, valor: marca });
  }

  /** Esquece tudo que desceu e baixa de novo do zero. */
  async ressincronizar(): Promise<void> {
    for (const colecao of COLECOES_QUE_DESCEM) {
      await this.deposito.apagar('config', `marca.${colecao}`);
    }
    await this.sincronizar();
  }
}
